import React from "react";
import { Box, Typography, Button } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { INDIGO, SLATE } from "../constants/Colors";

/**
 * Shared card for Clients and Agents grids.
 *
 * Props:
 *   logoSrc  — image URL
 *   title    — bold heading
 *   subtitle — colored accent text (e.g. "Higher Education")
 *   footer   — bold count line (e.g. "3 Agents")
 *   tags     — array of strings shown as chips
 *   onClick  — click handler
 *   viewUrl  — when set, shows a "View Agent" button that opens the URL in a new tab
 */
export default function EntityCard({ logoSrc, title, subtitle, footer, tags, onClick, viewUrl }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        border: `1px solid ${SLATE[200]}`,
        borderRadius: "12px",
        cursor: "pointer",
        overflow: "hidden",
        width: "100%",
        minHeight: 160,
        display: "flex",
        alignItems: "stretch",
        bgcolor: "#ffffff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
        "&:hover .entity-title": { color: INDIGO[600] },
      }}
    >
      {/* Left panel: logo */}
      <Box
        sx={{
          width: 112,
          flexShrink: 0,
          borderRight: `1px solid ${SLATE[100]}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#ffffff",
          p: 2,
          overflow: "hidden",
        }}
      >
        <img
          src={logoSrc}
          alt=""
          style={{ maxWidth: "100%", maxHeight: 80, objectFit: "contain", display: "block" }}
        />
      </Box>

      {/* Right panel: content */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: "16px 16px",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          overflow: "hidden",
        }}
      >
        <Typography
          className="entity-title"
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            color: SLATE[900],
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            transition: "color 0.15s ease",
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], fontWeight: 400 }}>
            {subtitle}
          </Typography>
        )}

        {footer && (
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900], mt: "8px" }}>
            {footer}
          </Typography>
        )}

        {tags?.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: "4px" }}>
            {tags.slice(0, 1).map((tag) => (
              <Box
                key={tag}
                sx={{
                  px: "8px",
                  py: "2px",
                  bgcolor: SLATE[100],
                  borderRadius: "4px",
                  fontSize: "0.6875rem",
                  color: SLATE[600],
                  fontWeight: 500,
                }}
              >
                {tag}
              </Box>
            ))}
            {tags.length > 1 && (
              <Box
                sx={{
                  px: "8px",
                  py: "2px",
                  bgcolor: INDIGO[50],
                  borderRadius: "4px",
                  fontSize: "0.6875rem",
                  color: INDIGO[600],
                  fontWeight: 500,
                }}
              >
                +{tags.length - 1} more
              </Box>
            )}
          </Box>
        )}

        {viewUrl && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto", pt: "8px" }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<OpenInNewIcon sx={{ fontSize: "0.75rem !important" }} />}
              onClick={(e) => {
                e.stopPropagation();
                window.open(viewUrl, "_blank");
              }}
              sx={{
                bgcolor: INDIGO[600],
                color: "white",
                fontSize: "0.6875rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "6px",
                boxShadow: "none",
                px: "10px",
                py: "3px",
                "&:hover": { bgcolor: INDIGO[700], boxShadow: "none" },
              }}
            >
              View Agent
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
